'use client';
import { ChangeEvent, useContext, useState } from 'react';
import { Button } from '@benzinga/core-ui';
import styled from '@benzinga/themetron';
import { IntercomManager } from '@benzinga/intercom-manager';
import { SessionContext } from '@benzinga/session-context';
import { TradingPhone, TechEnvelope } from '@benzinga/themed-icons';

export interface FusionHelpCardProps {
  email?: string;
  phone?: string;
  onSubmit?: (value: string) => void;
  phoneNumber?: string;
}

export const FusionHelpCard = ({
  email = 'onboarding@benzinga.com',
  onSubmit,
  phone = '(877) 440-9464',
  phoneNumber = '18774409464',
}: FusionHelpCardProps) => {
  const session = useContext(SessionContext);

  const [value, setValue] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<boolean>(false);

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => setValue(e.target?.value);

  const onSubmitHandler = async () => {
    if (onSubmit) {
      onSubmit(value);
      setSubmitError(false);
      setIsSubmitted(true);
    } else {
      const didSendMessage = await sendToIntercom(value);

      setSubmitError(!didSendMessage);
      setIsSubmitted(didSendMessage);
    }
  };

  const sendToIntercom = async (value: string): Promise<boolean> => {
    const options = {
      body: `Page: ${window.location.href}
      A submission was made to get expert help - find the right service for me.
      Contact: ${value}`,
      from: {
        id: '6679da5e3d506403be296f68',
        type: 'user',
      },
      referer: window.location.href,
      subject: 'Get Expert Help Request',
      to: {
        id: 2071945, // B2C Sales + All Text Responses
        type: 'team',
      },
    };

    const result = await session.getManager(IntercomManager).sendResearchIntercomMessage(options);

    if (result.err) {
      console.error(result.err);
      return false;
    }

    return true;
  };

  return (
    <HelpCardWrapper>
      <ContactSection>
        <BoxTitle>Ready to help you</BoxTitle>
        <BoxSubTitle>Contact our team of experts</BoxSubTitle>
        <Contacts>
          <ContactsWrapper>
            <TradingPhone className="help-form-icon" height="24" width="24" />
            <Contact href={`tel:+${phoneNumber}`}>{phone}</Contact>
          </ContactsWrapper>
          <ContactsWrapper>
            <TechEnvelope className="help-form-icon" height="24" width="24" />
            <Contact href={`mailto:${email}`}>{email}</Contact>
          </ContactsWrapper>
        </Contacts>
      </ContactSection>

      <FormSection>
        <BoxTitle>Not sure which service is right for you?</BoxTitle>
        <BoxSubTitle>Leave your details and we will contact you</BoxSubTitle>
        <InputsWrapper>
          <Input onChange={onChangeHandler} placeholder={'Phone number or Email'} value={value} />
          <Button onClick={onSubmitHandler} size="lg" variant="flat-blue">
            GET EXPERT HELP
          </Button>
        </InputsWrapper>
        {isSubmitted && (
          <p className="text-xs text-bzblue-800 mt-1">Thank you for your interest! We will contact you shortly.</p>
        )}
        {submitError && (
          <p className="text-xs text-red-600 mt-1">We could not send your request right now. Please try again later.</p>
        )}
      </FormSection>
    </HelpCardWrapper>
  );
};

const HelpCardWrapper = styled.div`
  display: flex;
  background-color: ${({ theme }) => theme.colorPalette.gray50};
  color: ${({ theme }) => theme.colorPalette.gray700};
  border: 1px solid ${({ theme }) => theme.colorPalette.gray200};
  box-shadow: 0 2px 8px rgba(25, 68, 128, 0.05);
  transition: 0.25s box-shadow ease-in-out;
  border-radius: 4px;

  &:hover {
    box-shadow: 0 2px 16px rgba(25, 68, 128, 0.1);
  }

  @media (max-width: 959px) {
    flex-direction: column;
  }
`;

const BoxTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  line-height: ${({ theme }) => theme.lineHeight[8]};
  color: ${({ theme }) => theme.colorPalette.gray700};
  margin-bottom: 4px;
`;

const BoxSubTitle = styled.p`
  color: ${({ theme }) => theme.colorPalette.gray500};
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.normal};
  line-height: ${({ theme }) => theme.lineHeight[5]};
  margin-bottom: 16px;
`;

const ContactSection = styled.div`
  width: 418px;
  flex-shrink: 0;
  padding: 24px;
  border-right: 1px solid ${({ theme }) => theme.colorPalette.gray200};

  @media (max-width: 1279px) {
    width: 340px;
  }

  @media (max-width: 959px) {
    width: 100%;
    border-right: 0;
    border-bottom: 1px solid ${({ theme }) => theme.colorPalette.gray200};
  }
`;

const FormSection = styled.div`
  flex: 1;
  padding: 24px;
`;

const ContactsWrapper = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;

  & .help-form-icon {
    color: ${({ theme }) => theme.colorPalette.blue500};
  }
`;

const Contact = styled.a`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  line-height: ${({ theme }) => theme.lineHeight[5]};
  margin-bottom: 0;
  margin-left: 10px;
  color: ${({ theme }) => theme.colorPalette.gray700};
`;

const Contacts = styled.div`
  @media (max-width: 959px) {
    display: flex;
    align-items: center;
    gap: 40px;
  }

  @media (max-width: 767px) {
    display: block;
    align-items: unset;
    gap: 0;
  }
`;

const InputsWrapper = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const Input = styled.input`
  flex-grow: 1;
  height: 56px;
  padding: 8px 18px;
  background-color: ${({ theme }) => theme.colorPalette.white};
  border: 1px solid ${({ theme }) => theme.colorPalette.gray100};
  border-radius: 4px;
  font-weight: ${({ theme }) => theme.fontWeight.normal};
  font-size: ${({ theme }) => theme.fontSize.lg};
  line-height: ${({ theme }) => theme.lineHeight[5]};
  color: ${({ theme }) => theme.colorPalette.gray400};
  transition: 0.25s box-shadow ease-in-out;

  &::placeholder {
    transition: 0.25s color ease-in-out;
  }

  &:hover {
    box-shadow: 0 2px 16px rgba(25, 68, 128, 0.1);

    &::placeholder {
      color: ${({ theme }) => theme.colorPalette.blue500};
    }
  }

  @media (max-width: 568px) {
    margin-right: 0;
    margin-bottom: 16px;
  }
`;
